import type { ReactNode, RefObject } from 'react'
import { AlertTriangle, Ban, CheckCircle2, Clock, Lock, Plus, ServerCrash, XCircle } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { hostOf, ownerPattern, providerLabel, suggestedCredentialName } from '@/lib/gitRouting'
import { cn } from '@/lib/utils'
import type { GitCredential, GitRoutingTestResult } from '@/types/gitRouting'
import type { RoutingRuleDraft } from './RoutingRuleDialog'

export interface TestUrlPanelProps {
  url: string
  onUrlChange: (url: string) => void
  onTest: (url: string) => void
  inputRef?: RefObject<HTMLInputElement>
  isTesting?: boolean
  result?: GitRoutingTestResult
  /** Why the test itself could not run — rate limit, a runtime that did not answer. */
  error?: string
  /** The tenant's tokens, to say when and by whom the matched one was set. */
  credentials?: GitCredential[]
  canEdit: boolean
  canManageTokens: boolean
  onAddRule: (draft: RoutingRuleDraft) => void
  onSetToken: (name: string, replacing: boolean) => void
}

/**
 * Which rule a URL matches, which credential it names, and whether that credential can read the
 * repository — a live, read-only call to the provider (GR4).
 */
export function TestUrlPanel({
  url,
  onUrlChange,
  onTest,
  inputRef,
  isTesting,
  result,
  error,
  credentials,
  canEdit,
  canManageTokens,
  onAddRule,
  onSetToken,
}: TestUrlPanelProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Test a URL</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <form
          className="flex gap-2"
          onSubmit={(e) => {
            e.preventDefault()
            if (url.trim()) {
              onTest(url.trim())
            }
          }}
        >
          <Input
            ref={inputRef}
            aria-label="Repository or pull request URL"
            placeholder="https://github.com/owner/repository/pull/1"
            value={url}
            onChange={(e) => onUrlChange(e.target.value)}
            autoComplete="off"
            spellCheck={false}
          />
          <Button type="submit" disabled={!url.trim() || isTesting}>
            {isTesting ? 'Testing…' : 'Test'}
          </Button>
        </form>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {result && !isTesting && (
          <TestResultCard
            result={result}
            credentials={credentials}
            canEdit={canEdit}
            canManageTokens={canManageTokens}
            onAddRule={onAddRule}
            onSetToken={onSetToken}
          />
        )}
      </CardContent>
    </Card>
  )
}

interface TestResultCardProps {
  result: GitRoutingTestResult
  credentials?: GitCredential[]
  canEdit: boolean
  canManageTokens: boolean
  onAddRule: (draft: RoutingRuleDraft) => void
  onSetToken: (name: string, replacing: boolean) => void
}

export function TestResultCard({
  result,
  credentials,
  canEdit,
  canManageTokens,
  onAddRule,
  onSetToken,
}: TestResultCardProps) {
  const provider = providerLabel(result.provider)
  const credential = result.credentialRef ?? ''

  if (!result.resolved) {
    return (
      <ResultFrame tone="bad" icon={<Ban className="h-5 w-5" />} title="No rule matches" testId="test-result-unresolved">
        <p>No tenant rule, deployment rule or host default covers this URL.</p>
        {result.problem && <p className="text-muted-foreground">{result.problem}</p>}
        {canEdit && (
          <Button size="sm" variant="outline" onClick={() => onAddRule({ urlPattern: patternForUnmatched(result.url) })}>
            <Plus className="mr-2 h-4 w-4" />
            Add rule
          </Button>
        )}
      </ResultFrame>
    )
  }

  const matched = <MatchedLines result={result} credentials={credentials} />
  const said = result.detail ? <p className="text-muted-foreground">{provider} said: “{result.detail}”</p> : null
  const addRuleForOwner = canEdit && result.owner && result.provider && (
    <Button
      size="sm"
      variant="outline"
      onClick={() => onAddRule({
        urlPattern: ownerPattern(result.url, result.owner),
        provider: result.provider ?? undefined,
        credentialName: suggestedCredentialName(result.provider ?? '', result.owner),
      })}
    >
      <Plus className="mr-2 h-4 w-4" />
      Add rule for this owner
    </Button>
  )
  const replaceToken = canManageTokens && credential && (
    <Button size="sm" variant="outline" onClick={() => onSetToken(credential, true)}>
      Replace token
    </Button>
  )

  switch (result.access) {
    case 'Readable':
      return (
        <ResultFrame tone="good" icon={<CheckCircle2 className="h-5 w-5" />} title="Readable" testId="test-result-Readable">
          {matched}
        </ResultFrame>
      )

    case 'NotFound':
      return (
        <ResultFrame tone="bad" icon={<XCircle className="h-5 w-5" />} title="Not found with this credential" testId="test-result-NotFound">
          {matched}
          <p>
            {provider} answered 404. A token that cannot see a private repository gets the same answer as a missing
            one. If the repository exists, add a rule that routes it to a credential owned by its organisation or user.
          </p>
          {addRuleForOwner}
        </ResultFrame>
      )

    case 'NotConfigured':
      return (
        <ResultFrame tone="warn" icon={<AlertTriangle className="h-5 w-5" />} title="Credential not set" testId="test-result-NotConfigured">
          {matched}
          <p>
            <span className="font-mono">{credential}</span> has no token yet. Nothing was sent to {provider}.
          </p>
          {canManageTokens && credential && (
            <Button size="sm" variant="outline" onClick={() => onSetToken(credential, false)}>
              Set token
            </Button>
          )}
        </ResultFrame>
      )

    case 'Unauthorized':
      return (
        <ResultFrame tone="bad" icon={<Lock className="h-5 w-5" />} title="Token rejected (401)" testId="test-result-Unauthorized">
          {matched}
          <p>The token is expired, revoked, or not valid for {provider}.</p>
          {said}
          {replaceToken}
        </ResultFrame>
      )

    case 'Forbidden':
      return (
        <ResultFrame tone="bad" icon={<Lock className="h-5 w-5" />} title="Not permitted (403)" testId="test-result-Forbidden">
          {matched}
          <p>The token is valid but lacks a permission this repository needs.</p>
          {said}
          {replaceToken}
        </ResultFrame>
      )

    case 'StoreUnavailable':
      // Deliberately nothing about the credential being missing: the store did not answer, and the token is
      // where it was (ADR 025 D10).
      return (
        <ResultFrame tone="warn" icon={<Clock className="h-5 w-5" />} title="Credential store unavailable" testId="test-result-StoreUnavailable">
          {matched}
          <p>
            The platform could not read credentials just now. This is not a configuration problem: try again in a
            minute. If it persists, contact your platform operator.
          </p>
        </ResultFrame>
      )

    case 'ProviderError':
      return (
        <ResultFrame tone="warn" icon={<ServerCrash className="h-5 w-5" />} title={`${provider} could not answer`} testId="test-result-ProviderError">
          {matched}
          <p>
            {provider} answered{result.providerStatus ? ` ${result.providerStatus}` : ''} without saying whether the
            token can read the repository — a rate limit or an outage on its side. Try again later.
          </p>
          {said}
        </ResultFrame>
      )

    default:
      return (
        <ResultFrame tone="warn" icon={<AlertTriangle className="h-5 w-5" />} title="Matched, not checked" testId="test-result-unchecked">
          {matched}
        </ResultFrame>
      )
  }
}

function MatchedLines({ result, credentials }: { result: GitRoutingTestResult; credentials?: GitCredential[] }) {
  const stored = credentials?.find((c) => c.name.toLowerCase() === (result.credentialRef ?? '').toLowerCase())
  const matched =
    result.source === 'TenantRule'
      ? `rule ${result.rulePosition} · ${result.pattern}`
      : result.source === 'DeploymentRule'
        ? `deployment rule · ${result.pattern}`
        : `deployment default · ${result.pattern}`

  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
      <dt className="text-muted-foreground">Matched</dt>
      <dd className="font-mono break-all">{matched}</dd>
      <dt className="text-muted-foreground">Provider</dt>
      <dd>
        {providerLabel(result.provider)}
        {result.owner && result.repository && (
          <span className="ml-4 text-muted-foreground">
            Repository <span className="font-mono text-foreground">{result.owner}/{result.repository}</span>
          </span>
        )}
      </dd>
      <dt className="text-muted-foreground">Credential</dt>
      <dd>
        <span className="font-mono">{result.credentialRef}</span>
        {stored && (
          <span className="ml-2 text-muted-foreground">
            (set {new Date(stored.updatedAt).toLocaleDateString()}{stored.setBy ? ` by ${stored.setBy}` : ''})
          </span>
        )}
      </dd>
    </dl>
  )
}

const tones = {
  good: 'border-green-300 bg-green-50 dark:border-green-900 dark:bg-green-950/40',
  warn: 'border-yellow-300 bg-yellow-50 dark:border-yellow-900 dark:bg-yellow-950/40',
  bad: 'border-red-300 bg-red-50 dark:border-red-900 dark:bg-red-950/40',
} as const

function ResultFrame({
  tone,
  icon,
  title,
  testId,
  children,
}: {
  tone: keyof typeof tones
  icon: ReactNode
  title: string
  testId: string
  children: ReactNode
}) {
  return (
    <div role="status" data-testid={testId} className={cn('space-y-2 rounded-lg border p-4 text-sm', tones[tone])}>
      <p className="flex items-center gap-2 font-semibold">
        {icon}
        {title}
      </p>
      {children}
    </div>
  )
}

/** For a URL nothing covers: its host and first path segment, as a pattern to start from. */
function patternForUnmatched(url: string): string {
  const host = hostOf(url)
  const owner = /^https?:\/\/[^/]+\/([^/]+)/i.exec(url)?.[1]
  return host ? `https://${host}/${owner ? `${owner}/` : ''}*` : ''
}
